import { Button, Typography } from "@suraasa/placebo-ui"
import { useMediaQuery } from "@uidotdev/usehooks"
import clsx from "clsx"
import AsyncBuilder from "components/AsyncBuilder"
import SuraasaPlusLogo, {
  LogoVariant,
} from "features/SuraasaPlus/components/SuraasaPlusLogo"
import { useSuraasaPlusContext } from "features/SuraasaPlus/context"
import { getSuraasaPlusPlans } from "features/SuraasaPlus/utils"
import { ArrowLeft } from "iconoir-react"
import { Link, useLocation } from "react-router-dom"
import routes from "routes"

import bgImage from "./assets/suraasa-settings-bg.webp"

const CompareFeatures = () => {
  const isSmDown = useMediaQuery("only screen and (max-width : 640px)")
  const location = useLocation()
  const {
    isFreeTrialAvailable,
    initiateSuraasaPlusPurchaseFlow,
    claimFreeTrial: instantlyClaimFreeTrial,
    aiToolGenerationsAllowed,
    jobApplicationsAllowed,
    isActive,
  } = useSuraasaPlusContext()

  const isCompareFeaturesPage =
    location.pathname === routes.suraasaPlusCompareFeat

  const plans = getSuraasaPlusPlans({
    isMobile: isSmDown,
    aiToolsFreeLimit: aiToolGenerationsAllowed,
    jobsFreeLimit: jobApplicationsAllowed,
  })

  return (
    <>
      {isCompareFeaturesPage && (
        <div className="mb-3">
          <Link to={routes.suraasaPlusSettings}>
            <Button
              variant="text"
              startAdornment={
                <ArrowLeft className="size-2.5" strokeWidth={2} />
              }
            >
              Back
            </Button>
          </Link>
        </div>
      )}
      <div className="relative overflow-hidden rounded-2xl bg-[radial-gradient(100%_100%_at_73.89%_0%,rgba(255,247,217,0.74)_0%,#FCFCFC_63.35%)] px-1 pt-7 sm:px-4">
        <img src={bgImage} alt="" className="absolute left-0 top-0" />
        <SuraasaPlusLogo height={36} width={200} color="dark" />
        <Typography variant="subtitle1" className="mb-6 mt-1.25 text-muted">
          Teaching Made Easier
        </Typography>
        {!isActive && (
          <>
            <AsyncBuilder
              handler={async () => {
                // Adding this check here because we don't want to show any sheet, we directly want to hit the API and claim the trial
                if (isFreeTrialAvailable) {
                  await instantlyClaimFreeTrial()
                  return
                }
                await initiateSuraasaPlusPurchaseFlow()
                return
              }}
              render={props => (
                <Button
                  color="black"
                  size="lg"
                  className="rounded-xl"
                  {...props}
                >
                  <Typography variant="strong">
                    {isFreeTrialAvailable
                      ? "Start 7-day free trial"
                      : "Get Suraasa Plus"}
                  </Typography>
                </Button>
              )}
            />
            {isFreeTrialAvailable && (
              <Typography
                variant="smallBody"
                className="mb-5 mt-1.25 max-w-[190px] text-muted"
              >
                1 tap to start, no credit card details required
              </Typography>
            )}
          </>
        )}
      </div>

      <div className="mb-8 sm:mb-6">
        <div className="grid grid-cols-2 gap-1 p-2 sm:grid-cols-[50%_25%_25%]">
          <div className="hidden sm:block" />
          <Typography variant="strongSmallBody" className="text-onSurface-700">
            Free
          </Typography>
          <SuraasaPlusLogo
            variant={LogoVariant.plus}
            width={36}
            height={17}
            color="#000000"
          />
        </div>

        {plans.map((plan, planIndex) => (
          <div
            key={planIndex}
            className={clsx("border-b border-onSurface-200 sm:border-none", {
              "border-none": planIndex === plans.length - 1,
            })}
          >
            <Typography
              variant={isSmDown ? "strongSmallBody" : "preTitle"}
              className="border-onSurface-300 px-2 py-1.5 text-muted sm:border-y sm:pb-2 sm:pt-4"
            >
              {plan.category}
            </Typography>

            {plan.items.map((item, itemIndex) => (
              <div
                key={itemIndex}
                className={clsx(
                  "my-2 grid h-5 grid-cols-2 items-start gap-1 border-b border-onSurface-200 px-2 sm:h-7 sm:grid-cols-[50%_25%_25%]",
                  { "border-none": itemIndex === plan.items.length - 1 }
                )}
              >
                <Typography
                  variant="subtitle2"
                  className="hidden text-onSurface-800 sm:block"
                >
                  {item.label}
                </Typography>
                <Typography
                  variant="smallBody"
                  className="max-w-[145px] text-onSurface-800 sm:max-w-[125px]"
                >
                  {item.free}
                </Typography>
                <Typography
                  variant={isSmDown ? "smallBody" : "strongSmallBody"}
                  className="bg-gradient-to-r from-[#A88824] to-[#E02BAA] bg-clip-text text-transparent"
                >
                  {item.plus}
                </Typography>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  )
}

export default CompareFeatures
